import { USAGETOPRODUCT, USAGETOWORK } from "../../../../Consts";
import { DataSource } from "../../../../types/DataSource";
import { IProductSearchResult } from "../../../../types/IProductSearchResult";
import { BulkMatchUsageType, IApplyMatchToMultipleRecordsRequest } from "../../../../types/usageTypes/IApplyMatchToMultipleRecordsRequest";
import { IBulkMatchTarget } from "../../../../types/usageTypes/IBulkMatchTarget";
import { IMatchWorksSearchResult } from "../../../../types/usageTypes/IMatchWorksSearchResult";
import { IUsageGroupResult } from "../../../../types/usageTypes/IUsageGroupsSearchResult";
import { IBulkTargetSearchDefaults } from "./usageBulkMatchTypes";

const emptyBulkTargetSearchDefaults =
    (): IBulkTargetSearchDefaults => ({
        title: "",
        artist: "",
        writers: "",
        productType: ""
    });

export const getSingleCommonValue = (values: Array<string | null | undefined>): string => {
    const normalizedValues = values
        .map(value => value?.trim())
        .filter(
            (value): value is string =>
                Boolean(value)
        );

    const distinctValues = Array.from(
        new Set(normalizedValues)
    );

    return distinctValues.length === 1
        ? distinctValues[0]
        : "";
};

export const deriveBulkTargetSearchDefaults = (
    usageGroupsSearchResult: | IUsageGroupResult[] | undefined,
    selectedUsageGroupIds: string[],
    usageType: string | undefined
): IBulkTargetSearchDefaults => {
    if (!usageGroupsSearchResult || selectedUsageGroupIds.length === 0) {
        return emptyBulkTargetSearchDefaults();
    }

    const selectedUsageGroups =
        usageGroupsSearchResult.filter(result =>
            selectedUsageGroupIds.includes(String(result.id))
        );

    if (selectedUsageGroups.length !== selectedUsageGroupIds.length) {
        return emptyBulkTargetSearchDefaults();
    }

    const title = getSingleCommonValue(
        selectedUsageGroups.map(result => result.title)
    );

    const artist = getSingleCommonValue(
        selectedUsageGroups.map(result => result.artist)
    );

    if (usageType === USAGETOWORK) {
        return {
            title,
            artist,
            writers: getSingleCommonValue(
                selectedUsageGroups.map(result => result.composer)
            ),
            productType: ""
        };
    }

    if (usageType === USAGETOPRODUCT) {
        return {
            title,
            artist,
            writers: "",
            productType: getSingleCommonValue(
                selectedUsageGroups.map(result => result.productType)
            )
        };
    }

    return emptyBulkTargetSearchDefaults();
};

export const mapWorkToBulkMatchTarget = (work: IMatchWorksSearchResult): IBulkMatchTarget => ({
    matchID: work.workID,
    entityType: "Work",
    dataSource: work.dataSource as DataSource,
    title: work.title?.join(", ") ?? "",
    number: work.number?.join(", ") ?? "",
    writers: work.writers?.join(", ") ?? "",
    publishers: work.publishers?.join(", ") ?? "",
    performers: work.artists?.join(", ") ?? "",
    targetStatus:  work.workStatus?.[0] ?? work.status ?? ""
});

export const mapProductToBulkMatchTarget = (product: IProductSearchResult): IBulkMatchTarget => ({
    matchID: product.productCoreID,
    entityType: "Product",
    dataSource: product.dataSource as DataSource,
    title: product.title?.join(", ") ?? "",
    number: product.number?.join(", ") ?? "",
    contributors: product.contributor?.join(", ") ?? "",
    performers: product.artist?.join(", ") ?? "",
    targetStatus: product.status ?? "",
    productType: product.type,
    id1: product.id1,
    id2: product.id2,
    episode: product.episode ?? "",
    worksCount: product.worksCount,
    yearOfProduction: product.yearOfProduction
});

export const normalizeSelectedUsageGroupIds = (selectedUsageGroupIds: string[]): string[] => {
    return Array.from(
        new Map(
            selectedUsageGroupIds
                .map(id => id.trim())
                .filter(Boolean)
                .map(id => [ id.toLocaleLowerCase(), id ] as const)
        ).values()
    );
};

const normalizeBulkMatchUsageType = (usageType: string | undefined): BulkMatchUsageType | null => {
    if (usageType === USAGETOWORK) {
        return "UsageToWork";
    }

    if (usageType === USAGETOPRODUCT) {
        return "UsageToProduct";
    }

    return null;
};

export const buildBulkMatchRequest = (
    usageType: string | undefined,
    selectedUsageGroupIds: string[],
    usageGroupsSearchResult: IUsageGroupResult[] | undefined,
    target: IBulkMatchTarget | null,
    createMatchHistory: boolean
): IApplyMatchToMultipleRecordsRequest | null => {
    const normalizedUsageType = normalizeBulkMatchUsageType(usageType);

    if (normalizedUsageType === null || target === null || !usageGroupsSearchResult) {
        return null;
    }

    const normalizedIDs = normalizeSelectedUsageGroupIds(selectedUsageGroupIds);

    if (normalizedIDs.length === 0 || target.matchID <= 0) {
        return null;
    }

    const selectedIDSet = new Set(normalizedIDs.map(id => id.toLocaleLowerCase()));

    const usageGroups = usageGroupsSearchResult
        .filter(result => selectedIDSet.has(result.id.toLocaleLowerCase()))
        .map(result => ({
            id: result.id,
            fullTitle: result.title ?? "",
            fullContributor: result.composer ?? "",
            fullPerformer: result.artist ?? "",
            sourceMajor: result.sourceMajor ?? "",
            sourceMinor: result.sourceMinor ?? ""
        }));

    if (usageGroups.length !== normalizedIDs.length) {
        return null;
    }

    if (normalizedUsageType === "UsageToProduct" && createMatchHistory && !target.productType?.trim()) {
        return null;
    }

    return {
        usageType: normalizedUsageType,
        usageGroups: usageGroups,
        matchID: target.matchID,
        targetProductType:
            normalizedUsageType === "UsageToProduct"
                ? target.productType
                : undefined,
        createMatchHistory
    };
};